/* Source: /tmp/jacbuilder-deploy-scratch/deploy-8asygg26/features/graph/GraphCanvas.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { Button } from "../../components/ui/button.js";
import { cn } from "../../lib/utils.js";
import { kind_title, kind_order, kind_fill, edge_stroke } from "./graph_helpers.js";
import { start_layout, layout_step, layout_moving, layout_drag, layout_positions, neighborhood } from "./graph_layout.js";
import { JacGraphViewer } from "./JacGraphViewer.js";
import { useState } from "@jac/runtime";
import { useRef } from "react";
import { useEffect } from "@jac/runtime";
import { _jac } from "@jac/prelude";
function GraphCanvas(props) {
  const {graph, selectedId, onSelect} = props;
  let ddx;
  let ddy;
  let qx;
  let qy;
  let tx;
  let ty;
  let tlen;
  let trim;
  let touching;
  let r;
  const [frame, setFrame] = useState(0);
  const [runs, setRuns] = useState(0);
  const [physics, setPhysics] = useState(true);
  const [view, setView] = useState("diagram");
  const simRef = useRef(null);
  const rafRef = useRef(null);
  const physicsRef = useRef(true);
  let width = 900.0;
  let height = 640.0;
  let legend_strip = 190.0;
  let crowded = (graph.nodes.length > 80);
  function animate() {
    rafRef.current = null;
    let sim = simRef.current;
    if (((sim && physicsRef.current) && layout_moving(sim))) {
      layout_step(sim);
      setFrame(sim["steps"]);
      rafRef.current = window.requestAnimationFrame(animate);
    }
  }
  function wake() {
    if ((rafRef.current == null)) {
      rafRef.current = window.requestAnimationFrame(animate);
    }
  }
  let signature = ((graph.nodes.map(n => n.id).join(",") + "|") + String(graph.edges.length));
  useEffect(() => {
    let sim = start_layout(graph.nodes, graph.edges, width, height, legend_strip);
    if (!physicsRef.current) {
      while (layout_moving(sim)) {
        layout_step(sim);
      }
    }
    simRef.current = sim;
    setFrame((frame + 1));
    wake();
  }, [signature, runs]);
  useEffect(() => {
    physicsRef.current = physics;
    if (physics) {
      wake();
    }
  }, [physics]);
  useEffect(() => {
    return () => {
      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current);
      }
    };
  }, []);
  function svg_point(svg, e) {
    let rect = svg.getBoundingClientRect();
    let pointer_x = e.clientX;
    let pointer_y = e.clientY;
    let left = rect.left;
    let top = rect.top;
    let box_w = rect.width;
    let box_h = rect.height;
    return [(((pointer_x - left) / box_w) * width), (((pointer_y - top) / box_h) * height)];
  }
  function press(e, node_id) {
    onSelect(node_id);
    let sim = simRef.current;
    if (!sim) {
      return;
    }
    let svg = e.currentTarget.ownerSVGElement;
    svg.setPointerCapture(e.pointerId);
    let p = svg_point(svg, e);
    layout_drag(sim, _jac.poly.getitem(sim["index"], node_id), p[0], p[1]);
    setFrame(sim["steps"]);
    wake();
  }
  function drag(e) {
    let sim = simRef.current;
    if ((!sim || (sim["drag"] < 0))) {
      return;
    }
    let p = svg_point(e.currentTarget, e);
    layout_drag(sim, sim["drag"], p[0], p[1]);
    setFrame(sim["steps"]);
    wake();
  }
  function release(e) {
    let sim = simRef.current;
    if (sim) {
      sim["drag"] = (-1);
    }
  }
  let positions = (simRef.current ? layout_positions(simRef.current) : {});
  let focus = neighborhood(graph.edges, selectedId);
  let kind_by_id = {};
  for (const n of graph.nodes) {
    kind_by_id[n.id] = n.kind;
  }
  let present = kind_order().filter(k => (graph.nodes.filter(n => (n.kind === k)).map(n => n).length > 0)).map(k => k);
  function radius(kind) {
    let base = ((kind === "Incident") ? 20.0 : ((kind === "Person") ? 16.0 : ((kind === "Claim") ? 13.0 : 11.0)));
    return (crowded ? (base * 0.55) : base);
  }
  function show_label(n) {
    return (!crowded || focus.includes(n.id));
  }
  function short_label(text) {
    return ((text.length <= 16) ? text : (text.slice(0, 15) + "\u2026"));
  }
  function fmt(v) {
    return String((Math.round((v * 10)) / 10));
  }
  let legend = __jacJsx("ul", {"className": "flex flex-col gap-1"}, [(() => {
    let __jac_view_kids = [false];
    __jac_view_kids[0] = (() => {
      let __jac_view_kids_1 = [];
      for (const k of present) {
        __jac_view_kids_1.push(__jacJsx("li", {"key": k, "className": "flex items-center gap-2"}, [__jacJsx("svg", {"viewBox": "0 0 10 10", "className": "size-3 shrink-0", "aria-hidden": "true"}, [__jacJsx("circle", {"cx": 5, "cy": 5, "r": 5, "className": kind_fill(k)}, [])]), kind_title(k)]));
      }
      return __jac_view_kids_1;
    })();
    return __jacJsx(null, {}, __jac_view_kids);
  })(), __jacJsx("li", {"className": "flex items-center gap-2 pt-1"}, [__jacJsx("svg", {"viewBox": "0 0 20 4", "className": "h-1 w-5 shrink-0", "aria-hidden": "true"}, [__jacJsx("line", {"x1": 0, "y1": 2, "x2": 20, "y2": 2, "className": "stroke-muted-foreground", "strokeWidth": 2}, [])]), "Stored edge"]), __jacJsx("li", {"className": "flex items-center gap-2"}, [__jacJsx("svg", {"viewBox": "0 0 20 4", "className": "h-1 w-5 shrink-0", "aria-hidden": "true"}, [__jacJsx("line", {"x1": 0, "y1": 2, "x2": 20, "y2": 2, "className": "stroke-primary", "strokeWidth": 2, "strokeDasharray": "4 3"}, [])]), "Derived / candidate"])]);
  return __jacJsx("figure", {"className": "flex flex-col gap-2"}, [__jacJsx("div", {"className": "overflow-hidden rounded-lg border border-border bg-card"}, [__jacJsx("div", {"className": "flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2"}, [__jacJsx("div", {"className": "flex flex-wrap items-center gap-2"}, [__jacJsx("span", {"className": "text-sm font-semibold"}, ["Jac graph"]), __jacJsx("span", {"className": "rounded-sm border border-primary/50 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary"}, [((view === "diagram") ? "This incident" : "Whole server")]), ((view === "diagram") && __jacJsx("span", {"className": "rounded-sm bg-muted px-2 py-0.5 text-xs text-muted-foreground"}, [(((String(graph.nodes.length) + " records, ") + String(graph.edges.length)) + " links")]))]), __jacJsx("div", {"className": "flex flex-wrap items-center gap-1.5"}, [((view === "diagram") && __jacJsx(Button, {"variant": "outline", "size": "sm", "onClick": () => {
    setRuns((runs + 1));
  }}, ["Re-run layout"])), ((view === "diagram") && __jacJsx(Button, {"variant": "outline", "size": "sm", "aria-pressed": physics, "onClick": () => {
    setPhysics(!physics);
  }}, [(physics ? "Physics on" : "Physics off")])), __jacJsx(Button, {"variant": ((view === "diagram") ? "default" : "outline"), "size": "sm", "aria-pressed": (view === "diagram"), "onClick": () => {
    setView("diagram");
  }}, ["Trace diagram"]), __jacJsx(Button, {"variant": ((view === "jac") ? "default" : "outline"), "size": "sm", "aria-pressed": (view === "jac"), "onClick": () => {
    setView("jac");
  }}, ["Jac's viewer"])])]), ((view === "jac") && __jacJsx(JacGraphViewer, {}, [])), ((view === "diagram") && __jacJsx("div", {"className": "relative"}, [__jacJsx("div", {"className": "overflow-x-auto"}, [__jacJsx("svg", {"viewBox": "0 0 900 640", "role": "img", "className": "block h-auto w-full min-w-[40rem] select-none", "onPointerMove": drag, "onPointerUp": release, "onPointerCancel": release, "aria-label": (((("Diagram of " + String(graph.nodes.length)) + " records and ") + String(graph.edges.length)) + " links. Use the record list below for keyboard access.")}, [__jacJsx("defs", {}, [__jacJsx("marker", {"id": "trace-graph-arrow", "viewBox": "0 0 10 10", "refX": "10", "refY": "5", "markerWidth": "7", "markerHeight": "7", "orient": "auto-start-reverse"}, [__jacJsx("path", {"d": "M0,0 L10,5 L0,10 z", "className": "fill-muted-foreground"}, [])])]), (() => {
    let __jac_view_kids_2 = [false];
    __jac_view_kids_2[0] = (() => {
      let __jac_view_kids_3 = [];
      for (const e of graph.edges) {
        if (((e.from_id in positions) && (e.to_id in positions))) {
          let a = positions[e.from_id];
          let b = positions[e.to_id];
          ddx = (b[0] - a[0]);
          ddy = (b[1] - a[1]);
          qx = (((a[0] + b[0]) / 2) - (ddy * 0.12));
          qy = (((a[1] + b[1]) / 2) + (ddx * 0.12));
          tx = (b[0] - qx);
          ty = (b[1] - qy);
          tlen = Math.max(Math.sqrt(((tx * tx) + (ty * ty))), 1.0);
          trim = (radius(kind_by_id[e.to_id]) + 3);
          touching = ((e.from_id === selectedId) || (e.to_id === selectedId));
          __jac_view_kids_3.push(__jacJsx("path", {"key": e.id, "fill": "none", "d": ((((((((((("M " + fmt(a[0])) + " ") + fmt(a[1])) + " Q ") + fmt(qx)) + " ") + fmt(qy)) + " ") + fmt((b[0] - ((tx / tlen) * trim)))) + " ") + fmt((b[1] - ((ty / tlen) * trim)))), "className": cn(edge_stroke(e.status), (touching ? "opacity-100" : "opacity-60")), "strokeWidth": (touching ? 2.5 : 1.2), "strokeDasharray": (e.derived ? "6 4" : null), "markerEnd": "url(#trace-graph-arrow)"}, []));
        }
      }
      return __jac_view_kids_3;
    })();
    return __jacJsx(null, {}, __jac_view_kids_2);
  })(), (() => {
    let __jac_view_kids_4 = [false];
    __jac_view_kids_4[0] = (() => {
      let __jac_view_kids_5 = [];
      for (const n of graph.nodes) {
        if ((n.id in positions)) {
          let p = positions[n.id];
          r = radius(n.kind);
          __jac_view_kids_5.push(__jacJsx("g", {"key": n.id, "className": "cursor-grab touch-none active:cursor-grabbing", "onPointerDown": e => press(e, n.id)}, [__jacJsx("title", {}, [((n.kind + ": ") + n.label)]), ((n.id === selectedId) && __jacJsx("circle", {"cx": p[0], "cy": p[1], "r": (r + 5), "className": "fill-none stroke-ring", "strokeWidth": 2.5}, [])), __jacJsx("circle", {"cx": p[0], "cy": p[1], "r": r, "className": cn(kind_fill(n.kind), "stroke-background/70"), "strokeWidth": 2}, []), (show_label(n) && __jacJsx("text", {"x": p[0], "y": ((p[1] + r) + 14), "textAnchor": "middle", "fontSize": 12, "className": cn("fill-foreground stroke-card", (focus.includes(n.id) ? "font-semibold" : "")), "strokeWidth": 3, "paintOrder": "stroke"}, [short_label(n.label)]))]));
        }
      }
      return __jac_view_kids_5;
    })();
    return __jacJsx(null, {}, __jac_view_kids_4);
  })()])]), __jacJsx("div", {"className": "pointer-events-none absolute bottom-3 left-3 hidden rounded-md border border-border bg-background/85 p-3 text-xs shadow-sm backdrop-blur-sm sm:block"}, [__jacJsx("p", {"className": "mb-1.5 font-semibold text-muted-foreground"}, ["Node types"]), legend])]))]), ((view === "diagram") && __jacJsx("figcaption", {"className": "flex flex-col gap-2 text-xs text-muted-foreground"}, [__jacJsx("p", {}, ["Drawn from the same Jac graph traversal as the record list. Drag a record and its neighbours follow; press one to inspect it. Arrows follow the stored edge direction; dashed links are computed on read, not stored."]), __jacJsx("div", {"className": "sm:hidden"}, [legend])]))]);
}
/*jac:refresh-boundary*/;
export {GraphCanvas};
//# sourceMappingURL=GraphCanvas.js.map
